/** SUNTRONIX ONE — Deploy → Select Device (Asset Age Intelligence, Phase 1).
 *  Rich device cards with purchase date, dynamic age, usage classification,
 *  deployment history, warranty, sort + quick-allocation modes, filters,
 *  explainable Recommend and side-by-side Compare. Tenant makes final choice.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Loader2, Laptop, ShieldCheck, ShieldAlert, Clock, History,
  Columns3, X, CheckCircle2, Building2, Plus,
} from "lucide-react";

const SORTS = [
  { v: "newest_purchase", l: "Newest purchase" },
  { v: "oldest_purchase", l: "Oldest purchase" },
  { v: "lowest_age", l: "Lowest asset age" },
  { v: "highest_age", l: "Highest asset age" },
  { v: "best_condition", l: "Best condition" },
  { v: "most_warranty", l: "Most warranty remaining" },
  { v: "least_deployed", l: "Least deployed" },
  { v: "most_deployed", l: "Most deployed" },
  { v: "recently_recovered", l: "Recently recovered" },
];
const MODES = [
  { l: "Manual", sort: "newest_purchase", usage: "" },
  { l: "Newest first", sort: "newest_purchase", usage: "" },
  { l: "Ageing first", sort: "oldest_purchase", usage: "" },
  { l: "Never deployed", sort: "newest_purchase", usage: "never_deployed" },
  { l: "Redeployment ready", sort: "oldest_purchase", usage: "redeployment_ready" },
  { l: "Best condition", sort: "best_condition", usage: "" },
  { l: "Most warranty", sort: "most_warranty", usage: "" },
];
const PREFS = [
  { v: "NO_PREFERENCE", l: "No preference" },
  { v: "PREMIUM", l: "Premium / Executive" },
  { v: "STANDARD", l: "Standard" },
  { v: "TRAINING_TEMP", l: "Training / Temporary" },
  { v: "NEW_STOCK", l: "New stock preferred" },
  { v: "AGEING_STOCK", l: "Ageing stock preferred" },
  { v: "REDEPLOYMENT", l: "Redeployment preferred" },
];
const AGE_LABEL_TONE = {
  NEWEST: "bg-emerald-100 text-emerald-800 border-emerald-200",
  CURRENT: "bg-sky-100 text-sky-800 border-sky-200",
  AGEING: "bg-amber-100 text-amber-800 border-amber-200",
  MATURE: "bg-orange-100 text-orange-800 border-orange-200",
  "REFRESH REVIEW": "bg-rose-100 text-rose-800 border-rose-200",
};
const USAGE_TONE = {
  "NEVER DEPLOYED": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "PREVIOUSLY DEPLOYED": "bg-slate-100 text-slate-700 border-slate-200",
  "REDEPLOYMENT READY": "bg-indigo-50 text-indigo-700 border-indigo-200",
  REFURBISHED: "bg-violet-50 text-violet-700 border-violet-200",
  REPAIRED: "bg-amber-50 text-amber-700 border-amber-200",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase() : null);
const imgUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${API}${u}`);

function DeviceCard({ c, selected, compareMode, inCompare, onToggle, recommended }) {
  const w = c.warranty || {};
  return (
    <div
      data-testid={`deploy-device-card-${c.id}`}
      className={`relative rounded-xl border bg-white p-3 transition-all ${selected ? "border-indigo-500 ring-2 ring-indigo-200" : recommended ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200 hover:border-slate-300"}`}
    >
      {recommended && (
        <span className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-600 text-white shadow-sm">
          <Sparkles className="h-2.5 w-2.5" /> RECOMMENDED
        </span>
      )}
      <div className="flex gap-3">
        <div className="h-16 w-16 shrink-0 rounded-lg bg-slate-50 border border-slate-100 grid place-items-center overflow-hidden">
          {c.image_url ? <img src={imgUrl(c.image_url)} alt={c.product_name} className="h-full w-full object-contain p-1" />
            : <Laptop className="h-7 w-7 text-slate-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 truncate">{c.product_name}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {c.colour ? `${c.colour} · ` : ""}{c.oem_part_number ? `${c.oem_part_number} · ` : ""}<span className="font-mono">{c.serial}</span>
              </div>
            </div>
            <button
              data-testid={`deploy-device-select-${c.id}`}
              onClick={() => onToggle(c.id)}
              className={`shrink-0 h-8 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1 transition-colors ${selected || inCompare ? "bg-indigo-600 text-white" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}
            >
              {selected || inCompare ? <><CheckCircle2 className="h-3.5 w-3.5" /> {compareMode ? "Added" : "Selected"}</> : (compareMode ? "Compare" : "Select")}
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {c.age_label && <Badge className={`text-[9px] border ${AGE_LABEL_TONE[c.age_label] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{c.age_label}</Badge>}
            <Badge className={`text-[9px] border ${USAGE_TONE[c.usage_status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{c.usage_status}</Badge>
            {(c.grade || c.condition) && <Badge className="text-[9px] border bg-slate-100 text-slate-700 border-slate-200">{c.grade || c.condition}</Badge>}
            {c.customer_owned && <Badge className="text-[9px] border bg-teal-50 text-teal-700 border-teal-200">{(c.owner || "CUSTOMER").toUpperCase()} OWNED</Badge>}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            <div className="flex items-center gap-1 text-slate-500"><Clock className="h-3 w-3" /> Purchase:</div>
            <div className={`text-right font-medium ${c.purchase_date ? "text-slate-800" : "text-rose-500"}`}>{fmtDate(c.purchase_date) || "NOT AVAILABLE"}</div>
            <div className="text-slate-500">Asset age:</div>
            <div className="text-right font-medium text-slate-800">{c.age_display}</div>
            <div className="flex items-center gap-1 text-slate-500"><History className="h-3 w-3" /> Deployments:</div>
            <div className="text-right font-medium text-slate-800">{c.previous_deployments}{c.last_recovery_date ? ` · rec ${fmtDate(c.last_recovery_date)}` : ""}</div>
            <div className="flex items-center gap-1 text-slate-500">{w.expired ? <ShieldAlert className="h-3 w-3 text-rose-500" /> : <ShieldCheck className="h-3 w-3 text-emerald-500" />} Warranty:</div>
            <div className={`text-right font-medium ${w.expired ? "text-rose-600" : "text-slate-800"}`}>{w.display}</div>
            <div className="flex items-center gap-1 text-slate-500"><Building2 className="h-3 w-3" /> Custody:</div>
            <div className="text-right font-medium text-slate-800 truncate">{c.custody || "—"}</div>
          </div>
          {c.purchase_date && c.purchase_date_status !== "VERIFIED" && (
            <div className="mt-1 text-[10px] text-amber-600">Source: {c.purchase_date_source} ({c.purchase_date_status})</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeviceSelectStep({ masterCompanyId, companyId, selectedIds, selectedAssets, onToggle, redeploy, setRedeploy, reloadSignal }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest_purchase");
  const [usage, setUsage] = useState("");
  const [ageBucket, setAgeBucket] = useState("");
  const [warranty, setWarranty] = useState("");
  const [mode, setMode] = useState("Manual");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pref, setPref] = useState("NO_PREFERENCE");
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/deployment/eligible-assets", {
        params: {
          master_company_id: masterCompanyId || undefined,
          company_id: companyId || undefined,
          q: q.trim() || undefined, sort,
          usage: usage || undefined, age_bucket: ageBucket || undefined,
          warranty_status: warranty || undefined, include_redeploy: redeploy || undefined,
          limit: 200,
        },
      });
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch { toast.error("Could not load eligible devices"); }
    finally { setLoading(false); }
  }, [masterCompanyId, companyId, q, sort, usage, ageBucket, warranty, redeploy]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, reloadSignal]);

  const applyMode = (m) => {
    const cfg = MODES.find((x) => x.l === m) || MODES[0];
    setMode(m); setSort(cfg.sort); setUsage(cfg.usage);
  };

  const doRecommend = async () => {
    setRecLoading(true); setRec(null);
    try {
      const { data } = await api.post("/deployment/recommend", {
        master_company_id: masterCompanyId || undefined,
        company_id: companyId || undefined, preference: pref,
        include_redeploy: redeploy || undefined, q: q.trim() || undefined, explain: true,
      });
      if (!data.recommended) { toast.info(data.message || "No match found"); }
      setRec(data);
    } catch { toast.error("Recommendation failed"); }
    finally { setRecLoading(false); }
  };

  const toggleCard = (id) => {
    if (compareMode) {
      setCompareIds((x) => x.includes(id) ? x.filter((i) => i !== id) : x.length >= 5 ? (toast.info("Compare up to 5 devices"), x) : [...x, id]);
    } else { onToggle(id); }
  };

  const compareRows = useMemo(() => items.filter((c) => compareIds.includes(c.id)), [items, compareIds]);

  const [seen, setSeen] = useState({});
  useEffect(() => { if (items.length) setSeen((s) => ({ ...s, ...Object.fromEntries(items.map((c) => [c.id, c])) })); }, [items]);
  const [addOpen, setAddOpen] = useState(false);
  const [addSerial, setAddSerial] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const addBySerial = async () => {
    const s = addSerial.trim(); if (!s) return;
    setAddBusy(true);
    try {
      const { data } = await api.get("/deployment/eligible-assets", { params: { master_company_id: masterCompanyId || undefined, company_id: companyId || undefined, q: s, include_redeploy: redeploy || undefined, limit: 20 } });
      const found = data.items || [];
      const hit = found.find((c) => [c.serial, c.asset_tag].some((v) => (v || "").toLowerCase() === s.toLowerCase())) || (found.length === 1 ? found[0] : null);
      if (!hit) { toast.error(found.length ? `${found.length} devices match "${s}" — type the full serial` : `No eligible device found for "${s}"`); return; }
      setSeen((m) => ({ ...m, [hit.id]: hit }));
      if (selectedIds.includes(hit.id)) toast.info(`${hit.serial} is already in this request`);
      else { onToggle(hit.id); toast.success(`${hit.serial} added · ${selectedIds.length + 1} device(s)`); }
      setAddSerial("");
    } catch { toast.error("Lookup failed"); }
    finally { setAddBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="deploy-device-intelligence">
      {/* summary strip */}
      {summary && (
        <div className="flex flex-wrap gap-2 text-[11px]" data-testid="deploy-summary">
          <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700">{summary.total} eligible</span>
          <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">{summary.never_deployed} never deployed</span>
          <span className="px-2 py-1 rounded-md bg-indigo-50 text-indigo-700">{summary.redeployment_ready} redeployment ready</span>
          <span className="px-2 py-1 rounded-md bg-amber-50 text-amber-700">{(summary.ageing_buckets["1to2y"] + summary.ageing_buckets["2to3y"] + summary.ageing_buckets["3y+"])} ageing</span>
          {summary.purchase_date_missing > 0 && <span className="px-2 py-1 rounded-md bg-rose-50 text-rose-600">{summary.purchase_date_missing} purchase date missing</span>}
        </div>
      )}

      <Input data-testid="deploy-device-search" placeholder="Search serial, tag, model, product…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10" />

      {/* quick allocation modes */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="deploy-quick-modes">
        {MODES.map((m) => (
          <button key={m.l} data-testid={`deploy-mode-${m.l.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => applyMode(m.l)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${mode === m.l ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {m.l}
          </button>
        ))}
      </div>

      {/* filters + sort */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={sort} onValueChange={(v) => { setSort(v); setMode("Manual"); }}>
          <SelectTrigger className="h-9 w-[190px] text-[12px]" data-testid="deploy-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={usage || "all"} onValueChange={(v) => setUsage(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[170px] text-[12px]" data-testid="deploy-filter-usage"><SelectValue placeholder="Usage" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All usage</SelectItem>
            <SelectItem value="never_deployed">Never deployed</SelectItem>
            <SelectItem value="redeployment_ready">Redeployment ready</SelectItem>
            <SelectItem value="previously_deployed">Previously deployed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={ageBucket || "all"} onValueChange={(v) => setAgeBucket(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[150px] text-[12px]" data-testid="deploy-filter-age"><SelectValue placeholder="Age" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any age</SelectItem>
            <SelectItem value="lt3m">&lt; 3 months</SelectItem>
            <SelectItem value="3to12m">3–12 months</SelectItem>
            <SelectItem value="1to2y">1–2 years</SelectItem>
            <SelectItem value="2to3y">2–3 years</SelectItem>
            <SelectItem value="3y+">3+ years</SelectItem>
          </SelectContent>
        </Select>
        <Select value={warranty || "all"} onValueChange={(v) => setWarranty(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[150px] text-[12px]" data-testid="deploy-filter-warranty"><SelectValue placeholder="Warranty" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any warranty</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <button data-testid="deploy-compare-toggle" onClick={() => { setCompareMode((v) => !v); setCompareIds([]); }}
          className={`h-9 px-3 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 border transition-colors ${compareMode ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
          <Columns3 className="h-3.5 w-3.5" /> Compare{compareIds.length ? ` (${compareIds.length})` : ""}
        </button>
      </div>

      {/* redeploy toggle */}
      <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer" data-testid="deploy-redeploy-toggle">
        <Checkbox checked={redeploy} onCheckedChange={(v) => setRedeploy(!!v)} />
        <span><b>Redeploy</b> — include devices currently assigned or returned (reassign to this employee)</span>
      </label>

      {/* recommend */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3" data-testid="deploy-recommend-panel">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-800 flex items-center gap-1"><Sparkles className="h-4 w-4 text-emerald-600" /> Deployment preference</span>
          <Select value={pref} onValueChange={setPref}>
            <SelectTrigger className="h-8 w-[210px] text-[12px]" data-testid="deploy-preference"><SelectValue /></SelectTrigger>
            <SelectContent>{PREFS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={doRecommend} disabled={recLoading} data-testid="deploy-recommend-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {recLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} Recommend device
          </Button>
        </div>
        {rec?.recommended && (
          <div className="mt-2 flex items-start justify-between gap-3 bg-white rounded-lg border border-emerald-200 p-2.5" data-testid="deploy-recommendation">
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-slate-900">Recommended: <span className="font-mono">{rec.recommended.serial}</span> · {rec.recommended.product_name}</div>
              {rec.ai_explanation && (
                <div className="mt-1 text-[11px] text-slate-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1.5" data-testid="deploy-ai-explanation">
                  <Sparkles className="h-3 w-3 inline mr-1 text-emerald-600" />{rec.ai_explanation}
                </div>
              )}
              <ul className="mt-1 text-[11px] text-slate-600 list-disc list-inside">
                {rec.recommended.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
              <div className="text-[10px] text-slate-400 mt-1">{rec.note}</div>
            </div>
            <Button size="sm" variant="outline" data-testid="deploy-recommend-select"
              onClick={() => { if (!selectedIds.includes(rec.recommended.asset_id)) onToggle(rec.recommended.asset_id); toast.success("Recommended device selected"); }}>
              Select this device
            </Button>
          </div>
        )}
      </div>

      {/* compare panel */}
      {compareMode && compareRows.length >= 2 && (
        <div className="rounded-xl border border-slate-200 overflow-x-auto" data-testid="deploy-compare-table">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500">
              <tr><th className="text-left p-2">Field</th>{compareRows.map((c) => <th key={c.id} className="text-left p-2 font-mono">{c.serial}<button onClick={() => setCompareIds((x) => x.filter((i) => i !== c.id))} className="ml-1 text-slate-400"><X className="h-3 w-3 inline" /></button></th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[["Purchase date", (c) => fmtDate(c.purchase_date) || "N/A"], ["Age", (c) => c.age_display], ["Usage", (c) => c.usage_status], ["Condition", (c) => c.grade || c.condition], ["Deployments", (c) => c.previous_deployments], ["Warranty", (c) => c.warranty.display], ["Last recovery", (c) => fmtDate(c.last_recovery_date) || "—"]].map(([label, fn]) => (
                <tr key={label}><td className="p-2 text-slate-500">{label}</td>{compareRows.map((c) => <td key={c.id} className="p-2 text-slate-800">{fn(c)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* cards */}
      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-slate-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading eligible devices…</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-slate-400 border border-dashed border-slate-200 rounded-xl" data-testid="deploy-no-devices">No eligible devices match these filters.</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5 max-h-[520px] overflow-y-auto pr-1" data-testid="deploy-device-grid">
          {items.map((c) => (
            <DeviceCard key={c.id} c={c}
              selected={selectedIds.includes(c.id)}
              compareMode={compareMode} inCompare={compareIds.includes(c.id)}
              recommended={rec?.recommended?.asset_id === c.id}
              onToggle={toggleCard} />
          ))}
        </div>
      )}
      {/* selected devices tray + Add Asset */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="deploy-selected-tray">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[12px] text-slate-500">Selected <span className="font-semibold text-slate-800" data-testid="deploy-selected-count">{selectedIds.length}</span> device(s) · You always make the final choice.</div>
          <Button size="sm" variant={addOpen ? "secondary" : "default"} className="ml-auto h-8 text-[12px]" onClick={() => { setAddOpen((v) => !v); setQ(""); }} data-testid="deploy-add-asset">
            <Plus className="h-3.5 w-3.5 mr-1" />Add Asset
          </Button>
        </div>
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedIds.map((id) => { const a = seen[id] || selectedAssets?.find((x) => x.id === id); return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] text-slate-800" data-testid={`deploy-selected-chip-${id}`}>
                <span className="font-mono">{a?.serial || a?.asset_tag || id.slice(0, 8)}</span>{a && <span className="text-slate-500">· {a.product_name || a.model || a.name}</span>}
                <button onClick={() => onToggle(id)} className="text-slate-400 hover:text-rose-600" title="Remove" data-testid={`deploy-selected-remove-${id}`}><X className="h-3 w-3" /></button>
              </span>); })}
          </div>
        )}
        {addOpen && (
          <div className="flex flex-wrap items-center gap-2" data-testid="deploy-add-asset-row">
            <Input autoFocus placeholder="Scan / type serial or asset tag, press Enter" value={addSerial} onChange={(e) => setAddSerial(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addBySerial()} className="h-9 w-80 font-mono text-[12px]" data-testid="deploy-add-asset-serial" />
            <Button size="sm" className="h-9" onClick={addBySerial} disabled={addBusy || !addSerial.trim()} data-testid="deploy-add-asset-submit">{addBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}</Button>
            <span className="text-[11px] text-slate-500">or click any card below to add it to this request</span>
          </div>
        )}
      </div>
    </div>
  );
}
